import { flatMap } from 'lodash';
import { queryElementsBySelector } from '../../page-insights/document_lookup';
import { ElementHighlighterConfig, DEFAULT_CONFIG } from './types';
import { HighlighterRect } from './highlighter_rect';
import { getFsElementFromFsIds } from './utils';
import { HighlightElementIdentifiers } from '@fullstory/renderer-shared';
import type { WebInspectorContext } from '../page-insights/inspector';

/**
 * Running inside the Iframe along side the DOM at a given frame.
 * It renders a element over another element with a border to highlight
 * that desired element.
 */
export class ElementHighlighter {
  private _highlightedElements: Element[] = [];
  private _highlighterRects: HighlighterRect[] = [];
  private _doc: Document;
  private _window: Window;
  private _config: ElementHighlighterConfig;
  private _status: 'active' | 'inactive' = 'inactive';
  private _onScrollFunctionRef: () => void;

  constructor(
    private _context: WebInspectorContext,
    config: ElementHighlighterConfig = DEFAULT_CONFIG,
  ) {
    const frame = this._context.activeRootDocFrame();
    if (!frame) {
      return;
    }
    this._doc = frame.contentDocument;
    this._window = frame.contentWindow;
    this._config = config;
  }

  /**
   * When we scroll in the Iframe we need to update the highlighter positioning.
   * The simplest way to do this is to dispose and highlight the elements
   */
  private _onScroll() {
    // We need onScroll to enclose the current `_highlightedElement reference`
    // To do this we return a function when listener is initialized
    return () => {
      this.highlightByElements([...this._highlightedElements]);
    };
  }

  /**
   * dispose of highlighters with out resetting document or listeners
   */
  dispose() {
    // adding to _highlightedElement or highlighterRect but keeping the same reference! DO NOT redefine so listener closer will stay up-to-date
    this._highlightedElements.length = 0;
    this._highlighterRects.forEach(elem => elem.dispose());
    this._highlighterRects.length = 0;
  }

  /**
   * resets the Window and Document element base of the active root frame
   */
  private _resetRender() {
    const frame = this._context.activeRootDocFrame();
    if (!frame) {
      // clean up
      this._doc = this._window = undefined;
      return;
    }
    this._doc = frame.contentDocument;
    this._window = frame.contentWindow;
  }

  /**
   * Starts the listeners and reset triggers a Window & Document reset based on flag passed in
   */
  start() {
    // if we start an active highlighter session then we
    // need to clean up the old listener and variable first
    this.stop();
    this._resetRender();
    // We need onScroll to enclose the current `_highlightedElement` reference
    // by doing this we return a new function when this is called.
    // Requiring us to save the reference to the function for cleanup via removeEventListener!
    this._onScrollFunctionRef = this._onScroll();
    this._window.addEventListener('scroll', this._onScrollFunctionRef, true);
    this._status = 'active';
  }

  /**
   * cleans up highlighter and listeners
   */
  stop() {
    // sanity check
    if (this._status !== 'active') {
      return;
    }
    this.dispose();
    this._window.removeEventListener('scroll', this._onScrollFunctionRef, true);
    this._onScrollFunctionRef = undefined;
    this._status = 'inactive';
  }

  /**
   * highlight any found element base on the selector strings passed in
   */
  highlightBySelectors(selectors: string[]) {
    // sanity check
    if (!this._doc || !this._window || this._status !== 'active' || !selectors) {
      return;
    }
    try {
      this.dispose();
      selectors.forEach(selector => {
        // Note: element can be found in any document linked to the current document
        // highlighter rect should be added to the corresponding elements document/dom
        const elementsToHighlightMatch = queryElementsBySelector(
          selector,
          undefined,
          undefined,
          undefined,
          this._doc,
          null,
        );
        const elementsToHighlight = flatMap(elementsToHighlightMatch, match => match.elements.e);
        elementsToHighlight.forEach(elem => {
          const newHighlighter = new HighlighterRect(elem, this._config);
          // adding to _highlightedElement or highlighterRect but keeping the same reference! DO NOT redefine so listener closer will stay up-to-date
          this._highlighterRects.push(newHighlighter);
          this._highlightedElements.push(elem);
        });
      });
    } catch (e) {
      console.error('Error: Unable to find highlight element', e);
    }
  }

  highlightByElements(elements: Element[]) {
    // sanity check
    if (!this._doc || !this._window || this._status !== 'active') {
      return;
    }
    this.dispose();
    elements.forEach(elem => {
      const newHighlighter = new HighlighterRect(elem, this._config);
      // adding to _highlightedElement or highlighterRect but keeping the same reference! DO NOT redefine so listener closer will stay up-to-date
      this._highlighterRects.push(newHighlighter);
      this._highlightedElements.push(elem);
    });
  }

  highlightByElement(element: Element) {
    this.highlightByElements([element]);
  }

  /**
   * Takes in an element identifier (fsid ids or css selectors) and highlights the elements they point too
   */
  highlightByElementsIdentifiers(elementIdentifiers: HighlightElementIdentifiers) {
    if (elementIdentifiers.type === 'css-selectors') {
      this.highlightBySelectors(elementIdentifiers.targetSelectors);
      return;
    }
    const fsElement = getFsElementFromFsIds(
      elementIdentifiers,
      this._context.activeFsRootDocument(),
    );

    if (fsElement) {
      this.highlightByElement(fsElement.dom);
    }
  }

  public get highlighters() {
    return this._highlighterRects;
  }
  public get highlightedElements() {
    return this._highlightedElements;
  }
}
